import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

const EFFECTIVE_DATE_RU = '16 мая 2026 г.';
const EFFECTIVE_DATE_EN = 'May 16, 2026';

const COMPANY = {
  full: 'ООО «Ресторан Теория»',
  ogrn: '1175029010650',
  inn: '5047197150',
  address: 'Московская обл., г. Долгопрудный, ул. Первомайская, д. 9/4, помещ. 8',
  email: 'manager@theorybar.ru',
};

function RuPolicy() {
  return (
    <div className="space-y-8 text-[15px] leading-relaxed text-gray-700">
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">1. Общие положения</h2>
        <p>
          {COMPANY.full} (ОГРН {COMPANY.ogrn}, ИНН {COMPANY.inn}, юридический адрес: {COMPANY.address}) разработало мобильное приложение «Теория» для оформления заказов в заведениях сети. Настоящая Политика конфиденциальности описывает, какие данные мы собираем, как используем и защищаем их.
        </p>
        <p className="mt-2">
          Используя приложение, вы соглашаетесь с условиями настоящей Политики. Если вы не согласны, пожалуйста, прекратите использование приложения.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">2. Собираемые данные</h2>
        <p className="font-medium text-gray-800 mb-2">2.1 Данные, которые вы предоставляете:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Номер телефона — для входа и идентификации</li>
          <li>Имя и фамилия — для персонализации заказов (необязательно)</li>
          <li>Адрес доставки — для оформления курьерских заказов</li>
        </ul>
        <p className="font-medium text-gray-800 mt-4 mb-2">2.2 Данные, собираемые автоматически:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Геолокация — для автоматического определения адреса доставки (только при явном разрешении)</li>
          <li>FCM-токен устройства — для отправки push-уведомлений о статусе заказа</li>
          <li>История заказов — для отображения в личном кабинете</li>
        </ul>
        <p className="font-medium text-gray-800 mt-4 mb-2">2.3 Платёжные данные:</p>
        <p>
          Платежи обрабатываются через сервис ЮKassa (ООО «Национальная платёжная корпорация»). Мы не храним данные банковских карт — они передаются напрямую в ЮKassa.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">3. Цели обработки данных</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left py-2 px-3 font-semibold text-gray-700 border border-gray-200">Данные</th>
                <th className="text-left py-2 px-3 font-semibold text-gray-700 border border-gray-200">Цель</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Номер телефона', 'Аутентификация и связь'],
                ['Геолокация', 'Автозаполнение адреса доставки'],
                ['Адрес доставки', 'Оформление и передача заказа'],
                ['FCM-токен', 'Push-уведомления о статусе заказа'],
                ['История заказов', 'Отображение в приложении'],
              ].map(([data, goal]) => (
                <tr key={data} className="border-b border-gray-100">
                  <td className="py-2 px-3 border border-gray-200">{data}</td>
                  <td className="py-2 px-3 border border-gray-200">{goal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">4. Передача данных третьим лицам</h2>
        <p className="mb-2">Мы передаём данные только в следующих случаях:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><span className="font-medium">Google Firebase</span> — для отправки push-уведомлений</li>
          <li><span className="font-medium">ЮKassa</span> — для обработки онлайн-платежей</li>
          <li><span className="font-medium">iiko</span> — для передачи заказа в систему ресторана</li>
          <li><span className="font-medium">По требованию закона</span> — если это обязывает российское законодательство</li>
        </ul>
        <p className="mt-3">Мы не продаём и не передаём ваши данные для маркетинговых целей третьим лицам.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">5. Хранение данных</h2>
        <p>
          Данные хранятся на серверах в России. Срок хранения — весь период использования аккаунта. При удалении аккаунта данные удаляются в течение 30 дней, за исключением данных, обязательных к хранению по законодательству РФ (ФЗ‑54, ФЗ‑152).
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">6. Геолокация</h2>
        <p>
          Приложение запрашивает доступ к геолокации только с вашего явного согласия и исключительно для автозаполнения адреса доставки. Геолокация используется только в момент активного использования приложения — в фоновом режиме не отслеживается.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">7. Push-уведомления</h2>
        <p>
          Push-уведомления отправляются только о статусе ваших заказов. Вы можете отключить их в настройках приложения или в системных настройках устройства в любой момент.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">8. Ваши права</h2>
        <p className="mb-2">В соответствии с ФЗ‑152 «О персональных данных» вы вправе:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Получить информацию о хранящихся данных</li>
          <li>Исправить неточные данные</li>
          <li>Запросить удаление данных</li>
          <li>Отозвать согласие на обработку</li>
        </ul>
        <p className="mt-3">Для реализации прав обратитесь: <a href={`mailto:${COMPANY.email}`} className="text-blue-600 underline">{COMPANY.email}</a></p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">9. Изменения политики</h2>
        <p>
          Мы можем обновлять данную Политику. При существенных изменениях уведомим через приложение или push-уведомление. Актуальная версия всегда доступна по данному адресу.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">10. Контакты</h2>
        <p>{COMPANY.full}</p>
        <p>ОГРН: {COMPANY.ogrn} · ИНН: {COMPANY.inn}</p>
        <p>{COMPANY.address}</p>
        <p>Email: <a href={`mailto:${COMPANY.email}`} className="text-blue-600 underline">{COMPANY.email}</a></p>
      </section>
    </div>
  );
}

function EnPolicy() {
  return (
    <div className="space-y-8 text-[15px] leading-relaxed text-gray-700">
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">1. General Provisions</h2>
        <p>
          {COMPANY.full} (OGRN {COMPANY.ogrn}, INN {COMPANY.inn}, legal address: {COMPANY.address}) has developed the «Теория» mobile application for placing orders at our restaurant locations. This Privacy Policy describes what data we collect, how we use it, and how we protect it.
        </p>
        <p className="mt-2">
          By using the application, you agree to the terms of this Policy. If you do not agree, please discontinue use of the application.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">2. Data We Collect</h2>
        <p className="font-medium text-gray-800 mb-2">2.1 Data you provide:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Phone number — for login and identification</li>
          <li>First and last name — for order personalisation (optional)</li>
          <li>Delivery address — for courier orders</li>
        </ul>
        <p className="font-medium text-gray-800 mt-4 mb-2">2.2 Data collected automatically:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Geolocation — to auto-fill the delivery address (only with your explicit permission)</li>
          <li>Device FCM token — to send push notifications about order status</li>
          <li>Order history — to display in your account</li>
        </ul>
        <p className="font-medium text-gray-800 mt-4 mb-2">2.3 Payment data:</p>
        <p>
          Payments are processed through YooKassa (LLC «National Payment Corporation»). We do not store bank card details — they are transmitted directly to YooKassa.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">3. Purposes of Data Processing</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left py-2 px-3 font-semibold text-gray-700 border border-gray-200">Data</th>
                <th className="text-left py-2 px-3 font-semibold text-gray-700 border border-gray-200">Purpose</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Phone number', 'Authentication and communication'],
                ['Geolocation', 'Auto-fill delivery address'],
                ['Delivery address', 'Order processing and dispatch'],
                ['FCM token', 'Push notifications about order status'],
                ['Order history', 'Display in the application'],
              ].map(([data, goal]) => (
                <tr key={data} className="border-b border-gray-100">
                  <td className="py-2 px-3 border border-gray-200">{data}</td>
                  <td className="py-2 px-3 border border-gray-200">{goal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">4. Sharing Data with Third Parties</h2>
        <p className="mb-2">We share data only in the following cases:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><span className="font-medium">Google Firebase</span> — to send push notifications</li>
          <li><span className="font-medium">YooKassa</span> — to process online payments</li>
          <li><span className="font-medium">iiko</span> — to transmit orders to the restaurant system</li>
          <li><span className="font-medium">As required by law</span> — when obligated by Russian legislation</li>
        </ul>
        <p className="mt-3">We do not sell or share your data for marketing purposes with third parties.</p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">5. Data Storage</h2>
        <p>
          Data is stored on servers located in Russia. Retention period covers the entire period of account use. Upon account deletion, data is removed within 30 days, except where retention is required by applicable Russian law (Federal Laws No. 54-FZ and No. 152-FZ).
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">6. Geolocation</h2>
        <p>
          The application requests access to geolocation only with your explicit consent and exclusively to auto-fill the delivery address. Geolocation is used only while the application is actively in use — it is not tracked in the background.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">7. Push Notifications</h2>
        <p>
          Push notifications are sent only regarding the status of your orders. You may disable them in the application settings or in your device's system settings at any time.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">8. Your Rights</h2>
        <p className="mb-2">In accordance with Federal Law No. 152-FZ «On Personal Data», you have the right to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Request information about stored data</li>
          <li>Correct inaccurate data</li>
          <li>Request deletion of data</li>
          <li>Withdraw consent to data processing</li>
        </ul>
        <p className="mt-3">To exercise your rights, contact us: <a href={`mailto:${COMPANY.email}`} className="text-blue-600 underline">{COMPANY.email}</a></p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">9. Policy Updates</h2>
        <p>
          We may update this Policy from time to time. For significant changes, we will notify you via the application or push notification. The current version is always available at this address.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">10. Contact</h2>
        <p>{COMPANY.full}</p>
        <p>OGRN: {COMPANY.ogrn} · INN: {COMPANY.inn}</p>
        <p>{COMPANY.address}</p>
        <p>Email: <a href={`mailto:${COMPANY.email}`} className="text-blue-600 underline">{COMPANY.email}</a></p>
      </section>
    </div>
  );
}

export default function PrivacyPolicyScreen() {
  const navigate = useNavigate();
  const [lang, setLang] = useState<'ru' | 'en'>('ru');

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-4 pb-16">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 flex items-center justify-between py-4 z-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft size={20} />
            <span className="text-sm">Назад</span>
          </button>

          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-sm font-medium">
            <button
              onClick={() => setLang('ru')}
              className={`px-3 py-1.5 transition-colors ${lang === 'ru' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              RU
            </button>
            <button
              onClick={() => setLang('en')}
              className={`px-3 py-1.5 transition-colors ${lang === 'en' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              EN
            </button>
          </div>
        </div>

        {/* Title */}
        <div className="py-8">
          <h1 className="text-2xl font-bold text-gray-900">
            {lang === 'ru' ? 'Политика конфиденциальности' : 'Privacy Policy'}
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            {lang === 'ru' ? `Действует с ${EFFECTIVE_DATE_RU}` : `Effective ${EFFECTIVE_DATE_EN}`}
          </p>
        </div>

        {lang === 'ru' ? <RuPolicy /> : <EnPolicy />}
      </div>
    </div>
  );
}
